import type { ReactNode } from "react"
import { Clock } from "lucide-react"

import type { CycleTime } from "@/admin/hooks/use-claims"
import { formatDuration } from "@/shared/lib/format-duration"

/**
 * WP1-67 cycle-time panel - a Clara Vale presentation of a claim's cycle-time
 * data (the "Insights" tab): a donut hero for the our-work vs awaiting-customer
 * split, a segmented ribbon for time across the stages, and refined
 * data-bar breakdowns by status and person.
 *
 * Read-only. Flag-gated upstream (``cycletime``). Fully generic - any tenant
 * with the flag on gets this exact panel; nothing here is client-specific.
 * Inline styles are used only for runtime-computed bar widths and donut arc
 * lengths (the sanctioned dynamic-value exception).
 */

function pct(part: number, whole: number): number {
  if (!whole || whole <= 0) return 0
  return Math.max(0, Math.min(100, (part / whole) * 100))
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <Clock className="h-3.5 w-3.5 text-[var(--green)]" />
      <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]">
        {children}
      </span>
    </div>
  )
}

function PanelCard({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-7">
      {children}
    </div>
  )
}

/** A coloured dot + label + value legend row. */
function LegendItem({
  color,
  label,
  value,
  share,
}: {
  color: string
  label: string
  value: string
  share?: number
}) {
  return (
    <div className="flex items-center gap-2.5">
      <span
        className={`h-2.5 w-2.5 shrink-0 rounded-full border-[0.5px] border-[var(--rule)] ${color}`}
      />
      <span className="text-[13px] text-[var(--ink-2)]">{label}</span>
      <span className="text-[15px] font-medium text-[var(--ink)]">{value}</span>
      {share != null && (
        <span className="text-[12px] text-[var(--ink-3)]">{Math.round(share)}%</span>
      )}
    </div>
  )
}

/** SVG donut: deep green = our work, soft green = awaiting customer; total centred. */
function SplitDonut({
  internal,
  external,
  centerLabel,
}: {
  internal: number
  external: number
  centerLabel: string
}) {
  const total = internal + external || 1
  const r = 68
  const c = 2 * Math.PI * r
  const ourWorkLen = (internal / total) * c
  const awaitingLen = (external / total) * c
  return (
    <div className="relative h-[164px] w-[164px] shrink-0">
      <svg viewBox="0 0 164 164" className="h-[164px] w-[164px] -rotate-90">
        <circle cx="82" cy="82" r={r} fill="none" stroke="var(--bg-subtle)" strokeWidth="15" />
        <circle
          cx="82"
          cy="82"
          r={r}
          fill="none"
          stroke="var(--green)"
          strokeWidth="15"
          style={{ strokeDasharray: `${ourWorkLen} ${c}` }}
        />
        <circle
          cx="82"
          cy="82"
          r={r}
          fill="none"
          stroke="var(--status-info-bg)"
          strokeWidth="15"
          style={{ strokeDasharray: `${awaitingLen} ${c}`, strokeDashoffset: `${-ourWorkLen}` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-[34px] font-normal leading-none text-[var(--ink)]">
          {centerLabel}
        </span>
        <span className="mt-1.5 text-[10px] uppercase tracking-[0.16em] text-[var(--ink-3)]">
          total
        </span>
      </div>
    </div>
  )
}

/** A clean row: leading + label on the left, value on the right. */
function StatRow({
  label,
  value,
  leading,
}: {
  label: string
  value: string
  leading?: ReactNode
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg bg-[var(--bg-subtle)] px-3.5 py-2.5">
      <div className="flex min-w-0 items-center gap-2.5">
        {leading}
        <span className="truncate text-[14px] text-[var(--ink)]">{label}</span>
      </div>
      <span className="shrink-0 text-[13px] font-medium text-[var(--ink-2)]">{value}</span>
    </div>
  )
}

export function ClaimCycleTimePanel({ cycleTime }: { cycleTime?: CycleTime | null }) {
  if (!cycleTime) {
    return (
      <PanelCard>
        <p className="text-center text-[14px] text-[var(--ink-3)]">
          No timing data yet for this claim.
        </p>
      </PanelCard>
    )
  }

  const { total_seconds, internal_seconds, external_seconds, is_open } = cycleTime
  const stages = cycleTime.by_stage.filter((b) => b.stage_key && b.total_seconds > 0)
  const statuses = cycleTime.by_status.filter((b) => b.total_seconds > 0)
  const people = cycleTime.by_user.filter((u) => u.internal_seconds > 0)
  const stageSum = stages.reduce((a, s) => a + s.total_seconds, 0) || 1

  return (
    <div className="flex flex-col gap-4">
      {/* Hero: donut + our-work-vs-awaiting split */}
      <PanelCard>
        <div className="flex items-center justify-between gap-3">
          <SectionLabel>Total time on this claim</SectionLabel>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--green-bg)] px-3 py-1 text-[12px] font-medium text-[var(--green)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--green)]" />
            {is_open ? "In progress" : "Closed"}
          </span>
        </div>
        <div className="mt-5 flex flex-col items-center gap-7 sm:flex-row sm:gap-10">
          <SplitDonut
            internal={internal_seconds}
            external={external_seconds}
            centerLabel={formatDuration(total_seconds)}
          />
          <div className="flex flex-col gap-4">
            <LegendItem
              color="bg-[var(--green)]"
              label="Our work"
              value={formatDuration(internal_seconds)}
              share={pct(internal_seconds, total_seconds)}
            />
            <LegendItem
              color="bg-[var(--status-info-bg)]"
              label="Awaiting customer"
              value={formatDuration(external_seconds)}
              share={pct(external_seconds, total_seconds)}
            />
            <p className="max-w-[280px] text-[12px] leading-relaxed text-[var(--ink-3)]">
              Green shows time our team spent working the claim. Cream shows
              time spent waiting on the customer.
            </p>
          </div>
        </div>
      </PanelCard>

      {/* Time by stage - one segmented ribbon */}
      {stages.length > 0 && (
        <PanelCard>
          <SectionLabel>Time by stage</SectionLabel>
          <div className="mt-5 flex h-10 w-full gap-1">
            {stages.map((s) => (
              <div
                key={s.stage_key}
                className={`h-full rounded-md border-[0.5px] border-[var(--rule)] ${
                  s.external_seconds > s.internal_seconds
                    ? "bg-[var(--status-info-bg)]"
                    : "bg-[var(--green)]"
                }`}
                style={{ width: `${pct(s.total_seconds, stageSum)}%` }}
              />
            ))}
          </div>
          <div className="mt-4 flex flex-col gap-2.5">
            {stages.map((s) => (
              <div key={s.stage_key} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <span
                    className={`h-2.5 w-2.5 shrink-0 rounded-full border-[0.5px] border-[var(--rule)] ${
                      s.external_seconds > s.internal_seconds
                        ? "bg-[var(--status-info-bg)]"
                        : "bg-[var(--green)]"
                    }`}
                  />
                  <span className="text-[14px] text-[var(--ink)]">{s.stage_label}</span>
                </div>
                <span className="text-[14px] font-medium text-[var(--ink-2)]">
                  {formatDuration(s.total_seconds)}
                </span>
              </div>
            ))}
          </div>
        </PanelCard>
      )}

      {/* By status + by person */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {statuses.length > 0 && (
          <PanelCard>
            <SectionLabel>By status</SectionLabel>
            <div className="mt-5 flex flex-col gap-2.5">
              {statuses.map((b) => (
                <StatRow
                  key={b.status_key}
                  label={b.status_label}
                  value={formatDuration(b.total_seconds)}
                />
              ))}
            </div>
          </PanelCard>
        )}

        {people.length > 0 && (
          <PanelCard>
            <SectionLabel>By person</SectionLabel>
            <div className="mt-5 flex flex-col gap-2.5">
              {people.map((u, i) => (
                <StatRow
                  key={u.user_id ?? `unassigned-${i}`}
                  label={u.name || "Unassigned"}
                  value={formatDuration(u.internal_seconds)}
                  leading={
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--green)] text-[11px] font-medium uppercase text-[var(--bg)]">
                      {(u.name || "U").trim().charAt(0)}
                    </span>
                  }
                />
              ))}
            </div>
          </PanelCard>
        )}
      </div>
    </div>
  )
}
